import { useState } from 'react';
import type { Drafts, Vessel, Corrections } from '../types';
import { calculateDraftSurvey, type DirectHydroValues } from '../lib/calculations';
import { HelpButton } from './HelpModal';
import { ArrowRight, Ruler, FileText } from 'lucide-react';
import OCRUpload from './OCRUpload';
import { parseOCRDrafts } from '../lib/ocr';

interface DraftInputProps {
  vessel: Vessel;
  onCalculate: (result: {
    displacement: number;
    corrections: Corrections;
    details: string[];
    warnings: string[];
    drafts: Drafts;
  }) => void;
  setHelpId?: (id: string | null) => void;
}

export default function DraftInput({ vessel, onCalculate, setHelpId }: DraftInputProps) {
  const [drafts, setDrafts] = useState<Drafts>({
    fwd_port: 0, fwd_stbd: 0,
    mid_port: 0, mid_stbd: 0,
    aft_port: 0, aft_stbd: 0,
    density: 1.025
  });
  const setHelpIdLocal = setHelpId || (() => {});
  const [directHydro, setDirectHydro] = useState<DirectHydroValues>({ displacement: 0, tpc: 0, mtc: 0, lcf: 0, dmtc: undefined });
  const useDirect = !vessel.hydrostatic || vessel.hydrostatic.length === 0;

  const update = (field: keyof Drafts, val: string) => {
    setDrafts((prev) => ({ ...prev, [field]: val === '' ? 0 : parseFloat(val) }));
  };

  const handleCalc = () => {
    // Check if all draft values are valid numbers (allow 0 as valid)
    const draftValues = [drafts.fwd_port, drafts.fwd_stbd, drafts.mid_port, drafts.mid_stbd, drafts.aft_port, drafts.aft_stbd];
    const hasInvalidDraft = draftValues.some(v => v === undefined || v === null || Number.isNaN(v));
    if (hasInvalidDraft) {
      alert('Please enter all 6 draft readings before calculating.');
      return;
    }

    // Check density
    if (!drafts.density || drafts.density <= 0) {
      alert('Please enter a valid density value (> 0).');
      return;
    }

    // Check direct hydro values if in direct mode
    if (useDirect) {
      if (!directHydro.displacement || !directHydro.tpc || !directHydro.mtc || directHydro.lcf === undefined) {
        alert('Please enter all 4 hydrostatic values (Displacement, TPC, MTC, LCF) before calculating.');
        return;
      }
    }

    try {
      const result = useDirect
        ? calculateDraftSurvey(drafts, vessel, directHydro)
        : calculateDraftSurvey(drafts, vessel);
      onCalculate({ ...result, drafts });
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Calculation failed. Please check your inputs.');
    }
  };

  const stations = [
    { key: 'fwd_port' as const, label: 'Fwd Port', help: 'draft-fwd-port' },
    { key: 'fwd_stbd' as const, label: 'Fwd Stbd', help: 'draft-fwd-stbd' },
    { key: 'mid_port' as const, label: 'Mid Port', help: 'draft-mid-port' },
    { key: 'mid_stbd' as const, label: 'Mid Stbd', help: 'draft-mid-stbd' },
    { key: 'aft_port' as const, label: 'Aft Port', help: 'draft-aft-port' },
    { key: 'aft_stbd' as const, label: 'Aft Stbd', help: 'draft-aft-stbd' },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="glass rounded-xl p-4 flex items-center gap-3">
        <Ruler className="w-5 h-5 text-ocean-400" />
        <div className="text-sm">
          <span className="text-navy-300 font-medium">{vessel.name}</span>
          <span className="text-navy-500 mx-2">•</span>
          <span className="text-navy-400">LBP {vessel.lbp}m</span>
        </div>
      </div>

      <OCRUpload
        type="drafts"
        label="Or upload a photo of draft marks"
        onResult={(result) => {
          if (result.structured) {
            const parsed = parseOCRDrafts(result.structured);
            setDrafts((prev) => ({ ...prev, ...parsed }));
          }
        }}
      />

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {stations.map((s) => (
          <div key={s.key} className="card-premium p-3">
            <label className="label-premium text-xs flex items-center">
              {s.label}
              <HelpButton helpId={s.help} onClick={setHelpIdLocal} />
            </label>
            <input
              type="number"
              step="0.001"
              className="input-premium"
              value={drafts[s.key] || ''}
              onChange={(e) => update(s.key, e.target.value)}
              placeholder="0.000"
            />
          </div>
        ))}
        <div className="card-premium p-3">
          <label className="label-premium text-xs flex items-center">
            Density
            <HelpButton helpId="density" onClick={setHelpIdLocal} />
          </label>
          <input
            type="number"
            step="0.001"
            className="input-premium"
            value={drafts.density || ''}
            onChange={(e) => update('density', e.target.value)}
            placeholder="1.025"
          />
        </div>
      </div>

      {useDirect && (
        <div className="glass rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <FileText className="w-4 h-4 text-ocean-400" />
            <h3 className="text-sm font-bold text-navy-200">Hydrostatic Values at Observed Draft</h3>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div className="card-premium p-3">
              <label className="label-premium text-xs">Displacement (MT)</label>
              <input type="number" step="0.01" className="input-premium" value={directHydro.displacement || ''} onChange={(e) => setDirectHydro(p => ({ ...p, displacement: (isNaN(parseFloat(e.target.value)) ? 0 : parseFloat(e.target.value)) }))} placeholder="0.00" />
            </div>
            <div className="card-premium p-3">
              <label className="label-premium text-xs">TPC</label>
              <input type="number" step="0.01" className="input-premium" value={directHydro.tpc || ''} onChange={(e) => setDirectHydro(p => ({ ...p, tpc: (isNaN(parseFloat(e.target.value)) ? 0 : parseFloat(e.target.value)) }))} placeholder="0.00" />
            </div>
            <div className="card-premium p-3">
              <label className="label-premium text-xs">MTC</label>
              <input type="number" step="0.01" className="input-premium" value={directHydro.mtc || ''} onChange={(e) => setDirectHydro(p => ({ ...p, mtc: (isNaN(parseFloat(e.target.value)) ? 0 : parseFloat(e.target.value)) }))} placeholder="0.00" />
            </div>
            <div className="card-premium p-3">
              <label className="label-premium text-xs">LCF (m)</label>
              <input type="number" step="0.01" className="input-premium" value={directHydro.lcf || ''} onChange={(e) => setDirectHydro(p => ({ ...p, lcf: (isNaN(parseFloat(e.target.value)) ? 0 : parseFloat(e.target.value)) }))} placeholder="+ = aft" />
            </div>
            <div className="card-premium p-3">
              <label className="label-premium text-xs">δMTC (optional)</label>
              <input type="number" step="0.01" className="input-premium" value={directHydro.dmtc || ''} onChange={(e) => setDirectHydro(p => ({ ...p, dmtc: parseFloat(e.target.value) || undefined }))} placeholder="For 2nd corr" />
            </div>
          </div>
          <p className="text-[10px] text-navy-500 mt-2">Read these from the hydrostatic table at the observed mean draft. European convention: +LCF = aft of midship.</p>
        </div>
      )}

      {!useDirect && (
        <div className="bg-ocean-600/5 border border-ocean-600/10 rounded-xl p-3">
          <p className="text-xs text-ocean-400">Using uploaded hydrostatic table ({vessel.hydrostatic?.length || 0} rows). Values will be interpolated automatically.</p>
        </div>
      )}

      <button type="button" onClick={handleCalc} className="btn-primary w-full flex items-center justify-center gap-2">
        Calculate Displacement <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  );
}
